

import * as React from "react";
import { cn } from "@/lib/utils";
import { ChevronLeft, ChevronRight, Menu, Minus, X } from "lucide-react";
import { Collapsible, CollapsibleContent } from "@/components/ui/collapsible";
import { Button, ButtonProps } from "@/components/ui/button";
import { useIsMobile } from "@/hooks/use-mobile";

// Create sidebar context for state management
type SidebarContextType = {
  expanded: boolean;
  toggleExpanded: () => void;
};

const SidebarContext = React.createContext<SidebarContextType | undefined>(
  undefined
);

export function useSidebar() {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
}

export interface SidebarProviderProps {
  children: React.ReactNode;
  defaultExpanded?: boolean;
}

export function SidebarProvider({
  children,
  defaultExpanded,
}: SidebarProviderProps) {
  const isMobile = useIsMobile();
  
  // Mobile always starts collapsed, desktop starts expanded
  const initialExpanded = defaultExpanded !== undefined 
    ? defaultExpanded 
    : !isMobile;
  
  const [expanded, setExpanded] = React.useState(initialExpanded);

  // Update expanded state when screen size changes
  React.useEffect(() => {
    // On mobile, always collapse when switching to mobile
    // On desktop, expand when switching from mobile
    if (isMobile) {
      setExpanded(false);
    } else if (defaultExpanded === undefined) {
      setExpanded(true);
    }
  }, [isMobile, defaultExpanded]);

  const toggleExpanded = React.useCallback(() => {
    setExpanded((prev) => !prev);
  }, []);

  // Prevent background scroll when mobile sidebar is open
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    if (isMobile && expanded) {
      document.body.classList.add("overflow-hidden", "touch-none");
    } else {
      document.body.classList.remove("overflow-hidden", "touch-none");
    }
    return () => {
      document.body.classList.remove("overflow-hidden", "touch-none");
    };
  }, [isMobile, expanded]);

  return (
    <SidebarContext.Provider value={{ expanded, toggleExpanded }}>
      {children}
    </SidebarContext.Provider>
  );
}

// Sidebar components
interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: "sidebar" | "collapsible-sidebar";
  collapsible?: "icon" | "none";
  children?: React.ReactNode;
}

export function Sidebar({
  variant = "sidebar",
  collapsible = "none",
  className,
  children,
  ...props
}: SidebarProps) {
  const { expanded, toggleExpanded } = useSidebar();
  const isMobile = useIsMobile();

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && expanded && (
        <div 
          className="fixed inset-0 bg-black/20 z-[60]"
          onClick={toggleExpanded}
        />
      )}
      
      <div
        className={cn(
          "flex flex-col bg-white transition-all duration-300 ease-in-out",
          // Mobile styles - always overlay on mobile
          isMobile && [
            "fixed left-0 top-0 h-full z-[70]",
            expanded 
              ? "w-64 translate-x-0 shadow-sm border-r" 
              : "w-0 -translate-x-full pointer-events-none invisible shadow-none border-0",
          ],
          // Desktop styles
          !isMobile && [
            "relative h-full",
            variant === "sidebar" && (
              expanded 
                ? "w-64 border-r shadow-sm" 
                : "w-0 overflow-hidden border-0 shadow-none"
            ),
          ],
          className
        )}
        aria-hidden={isMobile ? (!expanded) : (variant === "sidebar" && !expanded)}
        {...props}
      >
        {children}
      </div>
    </>
  );
}

export function SidebarHeader({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { expanded, toggleExpanded } = useSidebar();
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "flex items-center py-4 px-4 h-16 border-b transition-all duration-300",
        !expanded && !isMobile && "justify-center px-2",
        className
      )}
      {...props}
    >
      {/* Show content when expanded on desktop, or always on mobile */}
      {(expanded || isMobile) && (
        <div className="flex items-center justify-between w-full">
          {/* Show text only when expanded */}
          {expanded && (
            <div className="transition-opacity duration-300 opacity-100">
              {children}
            </div>
          )}
          {/* Show close button on mobile when expanded */}
          {isMobile && expanded && (
            <button
              onClick={toggleExpanded}
              className="ml-auto p-1 hover:bg-gray-100 rounded transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function SidebarContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();

  // On desktop, fully hide content when collapsed
  if (!expanded && !isMobile) {
    return null;
  }

  return (
    <div
      className={cn(
        "flex-1 p-3",
        isMobile 
          ? "overflow-y-auto max-h-[calc(100vh-8rem)]" 
          : "overflow-hidden",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SidebarFooter({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();

  // On desktop, fully hide footer when collapsed
  if (!expanded && !isMobile) {
    return null;
  }

  return (
    <div
      className={cn(
        "p-3 border-t transition-all duration-300",
        className
      )}
      {...props}
    >
      {/* Show content when expanded (mobile) or always (desktop when expanded) */}
      <div className={cn(
        "transition-all duration-300",
        // On mobile: only show when expanded
        isMobile && !expanded && "hidden"
      )}>
        {children}
      </div>
    </div>
  );
}

export function SidebarInset({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("flex-1 overflow-auto", className)}
      {...props}
    >
      {children}
    </div>
  );
}

// Added group components for better organization
export function SidebarGroup({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("mb-4", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function SidebarGroupLabel({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();

  if (!expanded && !isMobile) {
    return (
      <div className="flex justify-center py-2 px-2 text-xs text-gray-500">
        <Minus className="h-3 w-3" />
      </div>
    );
  }

  return (
    <div
      className={cn(
        "px-3 py-2 text-xs font-medium text-gray-500 uppercase tracking-wider transition-opacity duration-300",
        expanded ? "opacity-100" : "opacity-0",
        className
      )}
      {...props}
    >
      {children}
    </div>
  );
}

export function SidebarGroupContent({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("space-y-1", className)}
      {...props}
    >
      {children}
    </div>
  );
}

// Menu components
export function SidebarMenu({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn("py-1", className)}
      {...props}
    >
      {children}
    </div>
  );
}

export function SidebarMenuItem({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLLIElement>) {
  return (
    <li
      className={cn("list-none", className)}
      {...props}
    >
      {children}
    </li>
  );
}

// Submenu components
export function SidebarMenuSub({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLUListElement>) {
  return (
    <ul
      className={cn("ml-4 mt-1 space-y-1 border-l border-white/15 pl-3", className)}
      {...props}
    >
      {children}
    </ul>
  );
}

// Animated collapsible submenu wrapper
export function CollapsibleSidebarMenuSub({
  open,
  className,
  children,
}: {
  open: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Collapsible open={open}>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down">
        <SidebarMenuSub className={className}>{children}</SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function SidebarMenuSubItem({
  className,
  children,
  ...props
}: React.HTMLAttributes<HTMLLIElement>) {
  return (
    <li
      className={cn("list-none", className)}
      {...props}
    >
      {children}
    </li>
  );
}

export function SidebarMenuSubButton({
  className,
  children,
  isActive,
  asChild = false,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  isActive?: boolean;
  asChild?: boolean;
}) {
  const Element = asChild ? React.Fragment : "div";
  const buttonProps = asChild
    ? {}
    : {
        className: cn(
          "flex items-center gap-2 px-3 py-1.5 text-muted-foreground rounded-xl text-sm transition-all duration-300 cursor-pointer",
          "hover:bg-white/10 hover:backdrop-blur-sm",
          isActive && "bg-primary/10 text-primary backdrop-blur-sm border border-primary/15 shadow-[0_0_12px_0_hsl(var(--primary)/0.1)] font-medium",
          className
        ),
        ...props,
      };

  return <Element {...buttonProps}>{children}</Element>;
}

export interface SidebarMenuButtonProps
  extends React.HTMLAttributes<HTMLDivElement> {
  isActive?: boolean;
  tooltip?: string;
  asChild?: boolean;
  size?: ButtonProps["size"];
}

export function SidebarMenuButton({
  className,
  children,
  isActive,
  tooltip,
  asChild = false,
  size = "default",
  ...props
}: SidebarMenuButtonProps) {
  const { expanded } = useSidebar();
  const isMobile = useIsMobile();
  
  const Element = asChild ? React.Fragment : "div";
  const buttonProps = asChild
    ? {}
    : {
        className: cn(
          "flex items-center gap-2 px-3 py-2 text-sidebar-foreground rounded-xl text-sm font-medium transition-all duration-300 cursor-pointer",
          "hover:bg-white/10 hover:backdrop-blur-sm",
          isActive && (expanded || isMobile) && "bg-primary/10 text-primary backdrop-blur-sm border border-primary/15 shadow-[0_0_12px_0_hsl(var(--primary)/0.1)]",
          !expanded && !isMobile && "justify-center px-2",
          isMobile && "min-h-[44px]",
          className
        ),
        ...props,
      };

  const content = (
    <Element {...buttonProps}>
      {React.Children.map(children as React.ReactElement, (child) => {
        if (React.isValidElement(child)) {
          if (expanded || isMobile) {
            return child;
          } else {
            // If collapsed on desktop, only show the icon
            if (typeof child.type === "string") {
              return null;
            }
            return React.cloneElement(child as React.ReactElement, {
              className: cn("h-5 w-5", (child as React.ReactElement).props.className),
            });
          }
        }
        return child;
      })}
    </Element>
  );

  if (!expanded && !isMobile && tooltip) {
    return (
      <div className="group relative">
        {content}
        <span className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-2 py-1 rounded bg-gray-800 text-white text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
          {tooltip}
        </span>
      </div>
    );
  }

  return content;
}

export function SidebarTrigger({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  const { expanded, toggleExpanded } = useSidebar();
  const isMobile = useIsMobile();

  return (
    <div
      className={cn(
        "flex items-center justify-center rounded-md cursor-pointer transition-colors",
        isMobile 
          ? "h-10 w-10 bg-white hover:bg-gray-50 border border-gray-300 shadow-sm" 
          : "h-8 w-8 bg-amber-500 hover:bg-amber-600 shadow-sm text-white",
        className
      )}
      onClick={toggleExpanded}
      {...props}
    >
      {isMobile ? (
        <Menu className="h-5 w-5 text-gray-600" />
      ) : expanded ? (
        <ChevronLeft className="h-4 w-4" />
      ) : (
        <ChevronRight className="h-4 w-4" />
      )}
    </div>
  );
}
